import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

const PlanDetailsPage = () => {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchPlan = async () => {
      const ref = doc(db, 'plans', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setPlan({ id: snap.id, ...snap.data() });
      }
    };

    fetchPlan();
  }, [id]);

  const selectThisPlan = async () => {
    if (!currentUser || !plan) return;

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        selectedPlanId: plan.id,
        planSelectedAt: new Date(),
        planPausedDays: 0,
        isPlanPaused: false,
        planPausedAt: null
      });

      // Success animation/notification
      const successDiv = document.createElement('div');
      successDiv.innerHTML = '✅ Plan selected successfully!';
      successDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #48bb78, #38a169);
        color: white;
        padding: 1rem 2rem;
        border-radius: 10px;
        font-weight: 600;
        z-index: 1000;
        animation: slideIn 0.5s ease;
      `;
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        document.body.removeChild(successDiv);
        navigate('/customer/dashboard');
      }, 2000);
    } catch (error) {
      alert('Error selecting plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatFeatures = (features) => {
    if (!features) return [];
    
    // Ensure features is a string
    const featuresStr = typeof features === 'string' ? features : String(features);
    
    // Split by common delimiters and clean up
    return featuresStr
      .split(/[.!?]+/)
      .map(feature => feature.trim())
      .filter(feature => feature.length > 0)
      .map(feature => feature.charAt(0).toUpperCase() + feature.slice(1));
  };

  if (!plan) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '20px',
          textAlign: 'center',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #667eea',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#4a5568', fontSize: '1.1rem' }}>Loading plan details...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/customer/select-plan')}
          style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: 'none',
            padding: '0.8rem 1.5rem',
            borderRadius: '15px',
            fontSize: '1rem',
            cursor: 'pointer',
            marginBottom: '2rem',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255,255,255,0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
        >
          ← Back to Plans
        </button>

        {/* Main Content Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '25px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
        }}>
          {/* Hero Image Section */}
          <div style={{
            height: '300px',
            background: plan.imageUrl 
              ? `url(${plan.imageUrl})` 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end'
          }}>
            <div style={{
              position: 'absolute',
              inset: '0',
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)'
            }}></div>
            <div style={{
              position: 'relative',
              padding: '2rem',
              color: 'white'
            }}>
              <div style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '1rem',
                display: 'inline-block',
                backdropFilter: 'blur(10px)'
              }}>
                {plan.category}
              </div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                margin: '0',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}>
                {plan.name || 'Unnamed Plan'}
              </h1>
            </div>
          </div>

          {/* Content Section */}
          <div style={{ padding: '2.5rem' }}>
            {/* Price and Duration Section */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              padding: '1.5rem',
              backgroundColor: '#f8f9ff',
              borderRadius: '20px'
            }}>
              <div>
                <div style={{
                  fontSize: '3rem',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: '1'
                }}>
                  ₹{plan.price}
                </div>
                <p style={{
                  color: '#718096',
                  fontSize: '1.1rem',
                  margin: '0.5rem 0 0 0'
                }}>
                  One-time payment
                </p>
              </div>
              <div style={{
                textAlign: 'right'
              }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#4a5568'
                }}>
                  {plan.duration}
                </div>
                <p style={{
                  color: '#718096',
                  fontSize: '1.1rem',
                  margin: '0.5rem 0 0 0'
                }}>
                  days duration
                </p>
              </div>
            </div>

            {/* Description Section */}
            {plan.description && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#2d3748',
                  marginBottom: '1rem'
                }}>
                  About This Plan
                </h3>
                <p style={{
                  fontSize: '1.1rem',
                  lineHeight: '1.7',
                  color: '#4a5568',
                  backgroundColor: '#f7fafc',
                  padding: '1.5rem',
                  borderRadius: '15px',
                  margin: '0'
                }}>
                  {plan.description}
                </p>
              </div>
            )}

            {/* Features Section */}
            {plan.features && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#2d3748',
                  marginBottom: '1.5rem'
                }}>
                  What's Included
                </h3>
                <div style={{
                  backgroundColor: '#f8f9ff',
                  padding: '2rem',
                  borderRadius: '20px',
                  border: '2px solid #e2e8f0'
                }}>
                  {formatFeatures(plan.features).map((feature, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: index < formatFeatures(plan.features).length - 1 ? '1rem' : '0',
                        padding: '0.8rem',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <div style={{
                        backgroundColor: '#667eea',
                        color: 'white',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        marginRight: '1rem',
                        flexShrink: '0',
                        marginTop: '0.1rem'
                      }}>
                        ✓
                      </div>
                      <p style={{
                        margin: '0',
                        fontSize: '1rem',
                        lineHeight: '1.6',
                        color: '#4a5568'
                      }}>
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Button */}
            <div style={{
              textAlign: 'center',
              paddingTop: '2rem',
              borderTop: '2px solid #e2e8f0'
            }}>
              <button
                onClick={selectThisPlan}
                disabled={loading}
                style={{
                  background: loading 
                    ? 'linear-gradient(135deg, #a0aec0, #718096)' 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1.2rem 3rem',
                  borderRadius: '30px',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.4s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 15px 35px rgba(102, 126, 234, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 10px 25px rgba(102, 126, 234, 0.3)';
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '0.5rem'
                    }}></div>
                    Selecting...
                  </span>
                ) : (
                  'Select This Plan'
                )}
              </button>
              
              <p style={{
                color: '#718096',
                fontSize: '0.9rem',
                marginTop: '1rem',
                fontStyle: 'italic'
              }}>
                Start your transformation journey today!
              </p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PlanDetailsPage;